import { Loader2, LogIn } from "lucide-react";
import { Fragment } from "react";
import { ArcadeGrid } from "@/components/ui/atoms/arcade-grid";
import { Button } from "@/components/ui/atoms/button";
import { ArcadeLogo } from "@/components/ui/atoms/icons/arcade-logo";
import {
  AuthTemplate,
  AuthTemplateCard,
  AuthTemplateContent,
  AuthTemplateDescription,
  AuthTemplateFooter,
  AuthTemplateHeader,
  AuthTemplateLegal,
  AuthTemplateLegalLink,
  AuthTemplateLogo,
  AuthTemplatePrompt,
  AuthTemplatePromptLink,
  AuthTemplateTitle,
  AuthTemplateVideoBackground,
} from "@/components/ui/templates/auth-template";
import { cn } from "@/lib/utils";
import { PUBLIC_VIDEO_URL } from "@/metadata/constants";

const DEFAULT_VIDEO_SRC = `${PUBLIC_VIDEO_URL}/A_L_Orange_Pink.mp4`;

export type LoginPageProps = {
  /** Whether authentication is in progress */
  isLoading?: boolean;
  /** Text shown while loading (default: "Authenticating...") */
  loadingText?: string;
  /** Callback when the sign-in button is clicked */
  onSubmit: () => void;
  /** URL for the privacy policy link. Omit to hide. */
  privacyHref?: string;
  /** URL for the register link. Omit to hide the register prompt. */
  registerHref?: string;
  /** Text shown on the button (default: "Sign in with Arcade") */
  submitText?: string;
  /** URL for the terms link. Omit to hide legal section. */
  termsHref?: string;
  /** URL to the background video file. Defaults to the Arcade CDN-hosted video. Set to `undefined` to show only the gradient. */
  videoSrc?: string;
};

function ButtonIcon({ isLoading }: { isLoading: boolean }) {
  if (isLoading) {
    return <Loader2 className="size-5 animate-spin" />;
  }
  return <LogIn className="size-5" />;
}

function LegalLinks({
  termsHref,
  privacyHref,
}: {
  termsHref?: string;
  privacyHref?: string;
}) {
  type Link = { href: string; label: string };
  const links = [
    termsHref && { href: termsHref, label: "Terms of Service" },
    privacyHref && { href: privacyHref, label: "Privacy Policy" },
  ].filter((link): link is Link => Boolean(link));

  if (links.length === 0) {
    return null;
  }

  return (
    <AuthTemplateLegal>
      By continuing, you agree to the{" "}
      {links.map((link, i) => (
        <Fragment key={link.href}>
          {i > 0 && <span> and </span>}
          <AuthTemplateLegalLink href={link.href}>
            {link.label}
          </AuthTemplateLegalLink>
        </Fragment>
      ))}
    </AuthTemplateLegal>
  );
}

export function LoginPage({
  isLoading = false,
  loadingText = "Authenticating...",
  onSubmit,
  privacyHref,
  registerHref,
  submitText = "Sign in with Arcade",
  termsHref,
  videoSrc = DEFAULT_VIDEO_SRC,
}: LoginPageProps) {
  const buttonLabel = isLoading ? loadingText : submitText;
  const hasFooter = registerHref || termsHref || privacyHref;

  return (
    <AuthTemplate>
      {/* CSS gradient fallback visible while the video loads */}
      <div className="absolute -inset-4 z-0 bg-gradient-to-br from-rose-500/70 via-pink-400/50 to-amber-500/70 opacity-60 blur-lg brightness-50" />
      {videoSrc && <AuthTemplateVideoBackground src={videoSrc} />}
      <div className="absolute inset-0 z-1 opacity-[0.01]">
        <ArcadeGrid className="size-full" fill="currentColor" />
      </div>
      <AuthTemplateLogo>
        <ArcadeLogo className="h-12 w-auto text-zinc-900 dark:text-white" />
      </AuthTemplateLogo>
      <AuthTemplateCard>
        <AuthTemplateHeader>
          <AuthTemplateTitle>Welcome back</AuthTemplateTitle>
          <AuthTemplateDescription>
            Sign in with your Arcade account to continue
          </AuthTemplateDescription>
        </AuthTemplateHeader>

        <AuthTemplateContent>
          <Button
            className={cn(
              "group relative h-12 w-full overflow-hidden text-base transition-all duration-300",
              "hover:scale-[1.01] hover:shadow-md hover:brightness-105 active:scale-[0.99]"
            )}
            disabled={isLoading}
            onClick={onSubmit}
            size="lg"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              <ButtonIcon isLoading={isLoading} />
              {buttonLabel}
            </span>
            <div
              className={cn(
                "absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/0 to-primary/5 transition-opacity duration-300 group-hover:opacity-100",
                isLoading ? "pointer-events-none opacity-0" : "opacity-0"
              )}
            />
          </Button>
        </AuthTemplateContent>

        {hasFooter && (
          <AuthTemplateFooter>
            {registerHref && (
              <AuthTemplatePrompt>
                <span className="text-muted-foreground text-sm">
                  Don&apos;t have an account?
                </span>{" "}
                <AuthTemplatePromptLink href={registerHref}>
                  Register
                </AuthTemplatePromptLink>
              </AuthTemplatePrompt>
            )}

            <LegalLinks privacyHref={privacyHref} termsHref={termsHref} />
          </AuthTemplateFooter>
        )}
      </AuthTemplateCard>
    </AuthTemplate>
  );
}
